import React, {useEffect, useState} from "react";
import {useAuth} from "../../contexts/AuthContext";
import { IDSInput, IDSSelect, IDSButton, IDSCheckButton, IDSAlert } from '@inera/ids-react';
import StatusAlert from '../StatusAlert';
import {fetchOrganisations} from "../../services/adminAPI";
import { createUser } from "../../services/usersAdminAPI";
import { fetchDepartments, addDepartmentMember } from "../../services/departmentsAdminAPI";
import { REVIEWING_ORGANISATION } from "../../utils/reviewingOrganisation";
import { formatRoleName } from "../../utils/tokenUtils";
import { isValidEmail } from "../../utils/adminValidation";


const CreateUserLayout = () => {
    const { token } = useAuth();

    const [formData, setFormData] = useState({
        userName: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        organisationId: '',
        roleIds: [],
        departmentId: ''
    });
    const [organisations, setOrganisations] = useState([]);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [warning, setWarning] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        userName: null,
        email: null,
        password: null,
        organisationId: null,
        roleIds: null,
        departmentId: null
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear field error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await fetch('/api/admin/roles', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setRoles(Array.isArray(data) ? data : data.roles || []);
                }
            } catch (err) {
                console.error('Failed to fetch roles:', err);
                setFetchError('Kunde inte ladda roller. Ladda om sidan.');
                setRoles([]);
            }
        };

        if (token) {
            fetchRoles();
        }
    }, [token]);

    useEffect(() => {
        const loadOrganisations = async () => {
            try {
                const data = await fetchOrganisations();
                setOrganisations(data);
            } catch (err) {
                console.error('Failed to fetch organisations:', err);
                setFetchError('Kunde inte ladda organisationer. Ladda om sidan.');
                setOrganisations([]);
            }
        };

        if (token) {
            loadOrganisations().catch(err => {
                console.error('Unhandled error in fetchOrganisations:', err);
            })
        }
    }, [token]);

    // Departments only exist for the reviewing organisation
    useEffect(() => {
        const loadDepartments = async () => {
            try {
                const data = await fetchDepartments();
                setDepartments(data.filter(d => d.active));
            } catch (err) {
                console.error('Failed to fetch departments:', err);
                setDepartments([]);
            }
        };

        if (token) {
            loadDepartments();
        }
    }, [token]);

    // Reset selected roles and department when organisation changes
    useEffect(() => {
        setFormData(prev => ({ ...prev, roleIds: [], departmentId: '' }));
    }, [formData.organisationId]);

    // Compute roles filtered by selected organisation
    const filteredRoles = roles.filter(role => {
        if (!formData.organisationId) return false;

        const orgIdStr = String(formData.organisationId);
        const selectedOrg = organisations.find(o => String(o.id) === orgIdStr);
        const orgName = selectedOrg ? String(selectedOrg.name) : orgIdStr;
        const orgNameNormalized = orgName.toLowerCase().replace(/[_\s]/g, '');

        if (role.name && typeof role.name === 'string') {
            const roleNameLower = role.name.toLowerCase();
            const roleNameNormalized = roleNameLower.replace(/[_\s]/g, '');

            // Extract prefix before first separator
            const match = roleNameNormalized.match(/^([^-:]+)/);
            if (match && match[1] === orgNameNormalized) {
                return true;
            }
        }

        return false;
    });

    const selectedOrganisation = organisations.find(
        o => String(o.id) === String(formData.organisationId)
    );
    const isReviewingOrg = String(selectedOrganisation?.name || '').trim().toLowerCase()
        === REVIEWING_ORGANISATION.toLowerCase();
    const hasReviewerRole = filteredRoles.some(
        role => formData.roleIds.includes(role.id) && String(role.name || '').toLowerCase().includes('e2e-reviewer')
    );
    const showDepartmentPicker = isReviewingOrg && departments.length > 0;

    const validateForm = () => {
        const errors = { userName: null, email: null, password: null, organisationId: null, roleIds: null, departmentId: null };

        if (!formData.organisationId) {
            errors.organisationId = 'Välj en organisation.';
        }
        if (!formData.userName.trim()) {
            errors.userName = 'Användarnamn krävs.';
        } else if (formData.userName.trim().length < 3) {
            errors.userName = 'Användarnamnet måste vara minst 3 tecken.';
        }
        if (!formData.email.trim()) {
            errors.email = 'E-post krävs.';
        } else if (!isValidEmail(formData.email)) {
            errors.email = 'Ogiltig e-postadress. Ange t.ex. namn@exempel.se';
        }
        if (!formData.password) {
            errors.password = 'Lösenord krävs.';
        } else if (formData.password.length < 8) {
            errors.password = 'Lösenordet måste vara minst 8 tecken.';
        }
        if (formData.roleIds.length === 0) {
            errors.roleIds = 'Välj minst en roll.';
        }
        // The department is optional: it can just as well be set afterwards under
        // Avdelningar, so it must never block the user from being created.

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccess(false);
        setWarning(null);

        const errors = validateForm();
        setFieldErrors(errors);
        if (Object.values(errors).some(Boolean)) return;

        setLoading(true);
        setError(null);

        try {
            const { departmentId, ...userPayload } = formData;

            const created = await createUser({
                ...userPayload,
                userName: userPayload.userName.trim(),
                email: userPayload.email.trim(),
                firstName: userPayload.firstName.trim(),
                lastName: userPayload.lastName.trim(),
            });

            // The user lives in Keycloak while departments live in the E2E service,
            // so the membership is a separate call. The user is already created at
            // this point, hence a warning rather than an error if it fails.
            if (departmentId && created?.keycloakUserId) {
                try {
                    await addDepartmentMember(departmentId, created.keycloakUserId, {
                        name: [formData.firstName, formData.lastName]
                            .map(part => part.trim())
                            .filter(Boolean)
                            .join(' ') || formData.userName.trim(),
                        email: formData.email.trim(),
                    });
                } catch (deptErr) {
                    console.error('Failed to assign department:', deptErr);
                    setWarning('Användaren skapades, men avdelningen kunde inte sättas. Ange den under Avdelningar.');
                }
            } else if (departmentId) {
                setWarning('Användaren skapades, men avdelningen kunde inte sättas. Ange den under Avdelningar.');
            }

            setSuccess(true);
            setFormData({
                userName: '',
                email: '',
                firstName: '',
                lastName: '',
                password: '',
                organisationId: '',
                roleIds: [],
                departmentId: ''
            });
        } catch (err) {
            if (err.errorCode === 'MIMIR-3001') {
                setFieldErrors(prev => ({ ...prev, email: err.message }));
            } else if (err.errorCode === 'MIMIR-3003') {
                setFieldErrors(prev => ({ ...prev, userName: err.message }));
            } else {
                setError(err.message || 'Något gick fel. Försök igen.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <main className="ids-content ids-p-6" style={{ maxWidth: '600px', margin: '2rem auto' }}>
                <h1 className="ids-heading-l ids-mb-6">Skapa Användare</h1>

                <StatusAlert error={error} success={success ? 'Användare skapad!' : null} />
                {fetchError && <StatusAlert error={fetchError} />}
                {warning && (
                    <div className="ids-mb-4">
                        <IDSAlert dismissible ribbon type="warning">
                            <span>{warning}</span>
                        </IDSAlert>
                    </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                    <div className="ids-mb-4">
                        <IDSSelect
                            label="Organisation *"
                            id="organisationId"
                            name="organisationId"
                            value={formData.organisationId}
                            onChange={handleInputChange}
                        >
                            <option value="">Välj en organisation</option>
                            {organisations.map((org) => (
                                <option key={org.id} value={org.id}>
                                    {org.name}
                                </option>
                            ))}
                        </IDSSelect>
                        {fieldErrors.organisationId && (
                            <p style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                {fieldErrors.organisationId}
                            </p>
                        )}
                    </div>

                    <div className="ids-mb-4">
                        <p className="ids-label" style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
                            Roller * {formData.roleIds.length > 0 && (
                                <span style={{ fontWeight: 400, color: 'var(--ids-palette-neutral-60)', fontSize: '0.875rem' }}>
                                    ({formData.roleIds.length} {formData.roleIds.length === 1 ? 'vald' : 'valda'})
                                </span>
                            )}
                        </p>
                        {!formData.organisationId && (
                            <p className="ids-body" style={{ color: 'var(--ids-palette-neutral-60)', margin: 0 }}>Välj en organisation först</p>
                        )}
                        {formData.organisationId && filteredRoles.length === 0 && (
                            <p className="ids-body" style={{ color: 'var(--ids-palette-neutral-60)', margin: 0 }}>Ingen roll tillgänglig för vald organisation</p>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {filteredRoles.map((role) => (
                                <IDSCheckButton
                                    key={`${role.id}-${formData.roleIds.includes(role.id)}`}
                                    defaultChecked={formData.roleIds.includes(role.id)}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData(prev => ({
                                            ...prev,
                                            roleIds: checked
                                                ? [...prev.roleIds, role.id]
                                                : prev.roleIds.filter(id => id !== role.id)
                                        }));
                                        if (fieldErrors.roleIds) {
                                            setFieldErrors(prev => ({ ...prev, roleIds: null }));
                                        }
                                    }}
                                >
                                    {formatRoleName(role.name)}
                                </IDSCheckButton>
                            ))}
                        </div>
                        {fieldErrors.roleIds && (
                            <p style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                {fieldErrors.roleIds}
                            </p>
                        )}
                    </div>

                    {showDepartmentPicker && (
                        <div className="ids-mb-4">
                            <IDSSelect
                                label="Avdelning (valfritt)"
                                id="departmentId"
                                name="departmentId"
                                value={formData.departmentId}
                                onChange={handleInputChange}
                            >
                                <option value="">Ingen avdelning</option>
                                {departments.map((department) => (
                                    <option key={department.departmentId} value={department.departmentId}>
                                        {department.name}
                                    </option>
                                ))}
                            </IDSSelect>
                            <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-50)', marginTop: '0.25rem' }}>
                                Styr vilka E2E-formulär och inlämningar användaren ser som granskare.
                                Kan lämnas tom och sättas senare under Avdelningar.
                            </p>
                            {hasReviewerRole && !formData.departmentId && (
                                <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-status-warning-40)', marginTop: '0.25rem' }}>
                                    Utan avdelning ser granskaren alla ärenden tills en avdelning tilldelas.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="ids-mb-4">
                        <IDSInput
                            label="Användarnamn *"
                            invalid={!!fieldErrors.userName}
                            errorMsg={fieldErrors.userName}
                            type="text"
                            id="userName"
                            name="userName"
                            value={formData.userName}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="ids-mb-4">
                        <IDSInput
                            label="E-post *"
                            invalid={!!fieldErrors.email}
                            errorMsg={fieldErrors.email}
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="ids-mb-4">
                        <IDSInput
                            label="Förnamn"
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="ids-mb-4">
                        <IDSInput
                            label="Efternamn"
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="ids-mb-4">
                        <IDSInput
                            label="Lösenord *"
                            invalid={!!fieldErrors.password}
                            errorMsg={fieldErrors.password}
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                        />
                        <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-50)', marginTop: '0.25rem' }}>
                            Minst 8 tecken, både stora och små bokstäver samt minst en siffra.
                        </p>
                    </div>

                    <IDSButton size="m" disabled={loading} type="submit">
                        {loading ? 'Skapar...' : 'Skapa'}
                    </IDSButton>
                </form>
            </main>
        </div>
    );
};

export default CreateUserLayout;
